import { SeeqNames } from '@/main/app.constants.seeqnames';
import { BASE_TOOL_COMMON_PROPS } from '@/toolSelection/baseTool.constants';
import { BaseToolStore } from '@/toolSelection/baseTool.store';
import { TREND_TOOLS } from '@/toolSelection/investigate.constants';
import type { PersistenceLevel } from '@/core/flux.service';
import type { DerivedFeatureV1 } from '@/sdk/model/DerivedFeatureV1';
import type { InputFeatureV1, OutputTypeEnum } from '@/sdk/model/InputFeatureV1';
import type { ModelOutputV1 } from '@/sdk/model/ModelOutputV1';
import type { ReferencedOutputItemV1 } from '@/sdk/model/ReferencedOutputItemV1';
import type { ColumnDefinition } from '@/tableDefinitionEditor/tableDefinition.types';
import type { BaseToolPersistedState } from '@/toolSelection/baseTool.store';
import type { NewlyAddedInputFeature } from '@/tools/models/model.types';
import type { NewlyAddedDerivedFeature } from '@/tools/models/model.types';
import type { ModelDerivedFeature } from '@/tools/models/model.types';

export interface ModelInput {
  name: string;
  description?: string;
  referenceId?: string;
  id?: string;
  canonicalId?: string;
  isCollapsed?: boolean;
  originalName?: string;
  columnDefinition?: ColumnDefinition;
  outputType?: OutputTypeEnum;
  inputType?: string;
  parent?: string;
  inputItem?: string;
  inputNumeric?: number;
  inputNumericUom?: string;
  inputText?: string;
  inputBoolean?: boolean;
  isMapped?: boolean;
  mappedFeatureId?: string;
  selectedItem?: { name: string; id: string };
}

export interface ModelOutput extends ReferencedOutputItemV1 {
  id?: string;
  displayName?: string;
}

export type ModelItem = {
  name: string;
  description: string;
  derivedFeatures: ColumnDefinition[] | ModelOutput[] | DerivedFeatureV1[];
  inputFeatures: ModelInput[];
  existingDerivedFeatures: ModelDerivedFeature[];
  existingInputFeatures: ModelInput[];
  newDerivedFeatures: NewlyAddedDerivedFeature[];
  newInputFeatures: NewlyAddedInputFeature[];
};

// TODO: CRAB-56692: once the edit flow in Scaling Tables has updated types, remove derivedFeatures and
//  inputFeatures and just use existingDerivedFeatures and existingInputFeatures.
// TODO: CRAB-56765: also update types in create and apply files to use existing/new feature fields instead of derived/input features.
export type ModelPayload = Partial<
  Pick<
    ModelItem,
    | 'name'
    | 'description'
    | 'inputFeatures'
    | 'derivedFeatures'
    | 'existingDerivedFeatures'
    | 'existingInputFeatures'
    | 'newDerivedFeatures'
    | 'newInputFeatures'
  >
>;

interface ModelState extends BaseToolPersistedState {
  /** The model container GUID — the stable, canonical identity of the model across versions. */
  id: string;
  /** The GUID of the specific model version loaded in the store (e.g. a draft or the initial unlocked model). Used by the save flow to target editModelVersion and publishModelVersion. */
  versionId: string;
  description: string;
  inputFeatures: ModelInput[];
  derivedFeatures: ModelOutput[] | ColumnDefinition[] | DerivedFeatureV1[];
  name: string;
  scopedTo: string | undefined;
  featureToEditId: string | undefined;
  instanceToEditId: string | undefined;
  existingDerivedFeatures: ModelDerivedFeature[];
  existingInputFeatures: ModelInput[];
  newDerivedFeatures: NewlyAddedDerivedFeature[];
  newInputFeatures: NewlyAddedInputFeature[];
  instanceNameFeature: ModelInput | undefined;
  /** Whether the loaded model version is locked (immutable). */
  isLocked: boolean;
  /** Whether the loaded model is a draft (as opposed to the initial unlocked model). */
  isDraft: boolean;
  /** The production version number this draft was created from (e.g. 1 for "draft from v1"). */
  sourceVersionNumber: number | undefined;
}

export type ModelDehydratedState = ModelState;

export class ModelStore extends BaseToolStore {
  persistenceLevel: PersistenceLevel = 'WORKSHEET';

  type = TREND_TOOLS.MODEL_CREATE;

  initialize(): void {
    const initialState: ModelState = {
      ...BASE_TOOL_COMMON_PROPS,
      id: '',
      versionId: '',
      description: '',
      inputFeatures: [],
      derivedFeatures: [],
      name: '',
      scopedTo: undefined,
      featureToEditId: undefined,
      instanceToEditId: undefined,
      existingDerivedFeatures: [],
      existingInputFeatures: [],
      newDerivedFeatures: [],
      newInputFeatures: [],
      instanceNameFeature: undefined,
      isLocked: false,
      isDraft: false,
      sourceVersionNumber: undefined,
    };
    this.state = this.immutable(initialState);
  }

  get id(): string {
    return this.state.get('id');
  }

  get versionId(): string {
    return this.state.get('versionId');
  }

  get name(): string {
    return this.state.get('name');
  }

  get scopedTo(): string | undefined {
    return this.state.get('scopedTo');
  }

  get description(): string {
    return this.state.get('description');
  }

  get derivedFeatures(): ModelOutput[] | ColumnDefinition[] | DerivedFeatureV1[] {
    return this.state.get('derivedFeatures');
  }

  get inputFeatures(): ModelInput[] {
    return this.state.get('inputFeatures');
  }

  get featureToEditId(): string | undefined {
    return this.state.get('featureToEditId');
  }

  get instanceToEditId(): string | undefined {
    return this.state.get('instanceToEditId');
  }

  get existingDerivedFeatures(): ModelDerivedFeature[] {
    return this.state.get('existingDerivedFeatures');
  }

  get existingInputFeatures(): ModelInput[] {
    return this.state.get('existingInputFeatures');
  }

  get newDerivedFeatures(): NewlyAddedDerivedFeature[] {
    return this.state.get('newDerivedFeatures');
  }

  get newInputFeatures(): NewlyAddedInputFeature[] {
    return this.state.get('newInputFeatures');
  }

  get instanceNameFeature(): ModelInput | undefined {
    return this.state.get('instanceNameFeature');
  }

  get isLocked(): boolean {
    return this.state.get('isLocked');
  }

  get isDraft(): boolean {
    return this.state.get('isDraft');
  }

  get sourceVersionNumber(): number | undefined {
    return this.state.get('sourceVersionNumber');
  }

  dehydrate(): ModelDehydratedState {
    return this.state.serialize();
  }

  rehydrate(dehydratedState: Partial<ModelDehydratedState>): void {
    this.state.merge(dehydratedState);
  }

  protected readonly handlers = {
    ...this.baseHandlers,
  };

  resetModel = (): void => {
    this.initialize();
  };

  setFeatureToEditId = ({ featureToEditId }: { featureToEditId: string | undefined }): void => {
    this.state.set('featureToEditId', featureToEditId);
  };

  setInstanceToEditId = ({ instanceToEditId }: { instanceToEditId: string | undefined }): void => {
    this.state.set('instanceToEditId', instanceToEditId);
  };

  setVersionState = ({
    isLocked,
    isDraft,
    sourceVersionNumber,
  }: {
    isLocked: boolean;
    isDraft: boolean;
    sourceVersionNumber?: number;
  }): void => {
    this.state.set('isLocked', isLocked);
    this.state.set('isDraft', isDraft);
    this.state.set('sourceVersionNumber', sourceVersionNumber);
  };

  /** Sets the model output from the API response. */
  setModelOutput = ({ modelOutput }: { modelOutput: ModelOutputV1 }): void => {
    const { description, name, id, inputFeatures, derivedFeatures, scopedTo, versionId } = modelOutput;
    this.state.set('name', name);
    this.state.set('id', id);
    this.state.set('versionId', versionId ?? '');
    this.state.set('description', description);
    this.state.set('inputFeatures', inputFeatures);
    this.state.set('derivedFeatures', derivedFeatures);
    this.state.set('scopedTo', scopedTo);
    this.state.set('existingDerivedFeatures', derivedFeatures);
    this.state.set(
      'existingInputFeatures',
      inputFeatures?.filter((feature) => feature.name !== SeeqNames.Models.InstanceNameInputFeatureName),
    );
    this.state.set(
      'instanceNameFeature',
      inputFeatures?.find((input) => input.name === SeeqNames.Models.InstanceNameInputFeatureName),
    );
    this.state.set('newInputFeatures', []);
    this.state.set('newDerivedFeatures', []);
  };

  /** Sets the model fields from the provided model payload, which may be partial. Only fields present in the payload will be updated. */
  setModel = ({ model }: { model: ModelPayload }): void => {
    const {
      description,
      name,
      inputFeatures,
      derivedFeatures,
      existingDerivedFeatures,
      newInputFeatures,
      newDerivedFeatures,
      existingInputFeatures,
    } = model;

    if (description !== undefined) this.state.set('description', description);
    if (name !== undefined) this.state.set('name', name);
    if (derivedFeatures !== undefined) this.state.set('derivedFeatures', derivedFeatures);
    if (existingDerivedFeatures !== undefined) this.state.set('existingDerivedFeatures', existingDerivedFeatures);
    if (existingInputFeatures !== undefined) this.state.set('existingInputFeatures', existingInputFeatures);
    if (newInputFeatures !== undefined) this.state.set('newInputFeatures', newInputFeatures);
    if (newDerivedFeatures !== undefined) this.state.set('newDerivedFeatures', newDerivedFeatures);

    if (inputFeatures !== undefined) {
      const newInputFeatures = inputFeatures ?? [];

      // CRAB-53616: Ensure the instance name input feature is preserved
      const instanceName = this.state
        .get('inputFeatures')
        .find((f: InputFeatureV1) => f.name === SeeqNames.Models.InstanceNameInputFeatureName);

      const updatedInputFeatures =
        instanceName && !newInputFeatures.some((f) => f.name === SeeqNames.Models.InstanceNameInputFeatureName)
          ? [...newInputFeatures, instanceName]
          : newInputFeatures;

      this.state.set('inputFeatures', updatedInputFeatures);
    }
  };
}
